import * as React from 'react'

import { Box, Checkbox, SearchField, Text } from '@factorialco/deprecated-design-system'
import isEmpty from 'lodash/isEmpty'

import Chip from 'modules/core/components/Filter/base/Chip'
import ClearValue from 'modules/core/components/Filter/base/ClearValue'
import Content from 'modules/core/components/Filter/base/Content'
import Provider from 'modules/core/components/Filter/base/Provider'
import useFilter from 'modules/core/components/Filter/base/useFilter'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { matchText } from 'modules/core/lib/stringUtils'

import './index.styles.css'
type Option<T> = {
  label: string
  value: T
  component?: React.ReactNode
}

type Props<T> = {
  header?: React.ReactNode
  footer?: React.ReactNode
  label: string
  options: Option<T>[]
  value?: T[]
  selectedOptions?: Option<T>[]
  onValueChange: (value?: T[]) => void
  defaultOpen?: boolean
  hideSearch?: boolean
  hideFooter?: boolean
  onToggle?: (open: boolean) => void
  fullWidth?: boolean
  onClose?: () => void
  composeActiveLabel?: (foundOptions: Option<T>[]) => string
}

type BodyProps<T> = {
  options: Option<T>[]
  hideSearch?: boolean
}

function Body<T>({ options, hideSearch }: BodyProps<T>) {
  const { value: filterValue, onValueChange } = useFilter<T[]>()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const onClick = (value: T) => {
    const filteredValue = filterValue!

    if (!filteredValue.includes(value)) {
      return onValueChange(filteredValue.concat(value))
    } else {
      return onValueChange(filteredValue.filter((_value) => value !== _value))
    }
  }

  const filterBySearch = (options: Option<T>[]) => {
    if (isEmpty(debouncedSearch)) return options

    return options.filter((option) => matchText(debouncedSearch, option.label))
  }

  return (
    <Box flexDirection="column" gapDivider overflowY="hidden">
      {!hideSearch && (
        <Box padding="s12">
          <SearchField variant="big" onSearch={onSearch} search={search} placeholder="" />
        </Box>
      )}
      <Box overflowY="scroll" hideScrollBar>
        {filterBySearch(options).map(({ label, value, component }, index) => (
          <Box component="label" key={index} className={'MultipleChoice_option'}>
            <Box flexDirection="row" alignItems="center" gap="s16">
              <Checkbox
                checked={filterValue!.includes(value)}
                onChange={() => onClick(value)}
                size="large"
              />
              {component ?? (
                <Text.Normal size="300" color="grey800" ellipsis>
                  {label}
                </Text.Normal>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function MultipleChoice<T>({
  header,
  label,
  options,
  value,
  selectedOptions,
  onValueChange,
  defaultOpen,
  hideSearch,
  footer,
  hideFooter = false,
  onToggle,
  fullWidth,
  onClose,
  composeActiveLabel,
}: Props<T>) {
  const foundOptions = selectedOptions || options.filter((option) => value?.includes(option.value))

  const getActiveLabel = () => {
    if (composeActiveLabel) return composeActiveLabel(foundOptions)
    if (foundOptions.length === 1 && foundOptions[0]) return foundOptions[0].label
    return `(${foundOptions.length})`
  }

  const selectedValues = foundOptions.map((option) => option.value)

  return (
    <Provider
      value={selectedValues}
      onValueChange={onValueChange}
      closeOnChange={false}
      defaultOpen={!!defaultOpen}
    >
      <Chip.Toggler
        active={selectedValues.length > 0}
        label={label}
        activeLabel={getActiveLabel()}
        onToggle={onToggle}
        fullWidth={fullWidth}
      />
      <Content
        onClose={onClose}
        header={header}
        body={<Body options={options} hideSearch={hideSearch} />}
        footer={hideFooter ? '' : footer || <ClearValue />}
      />
    </Provider>
  )
}

export default MultipleChoice
